import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { ChevronDown, Plus, X } from "lucide-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  getCurrencySymbol,
  MonetaryAmountInput,
  MonetaryDisplay,
} from "@/components/ui/monetary-display";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

import UserSelector from "@/components/Common/UserSelector";

import { useShortcutSubContext } from "@/context/ShortcutContext";
import useCurrentFacility from "@/pages/Facility/utils/useCurrentFacility";
import {
  conditionSchema,
  getConditionDiscriminatorValue,
} from "@/types/base/condition/condition";
import {
  convertDiscountValue,
  getDiscountableAmount,
  hasExcessiveDiscount,
  isPercentageBased,
  MonetaryComponent,
  MonetaryComponentType,
} from "@/types/base/monetaryComponent/monetaryComponent";
import {
  ChargeItemRead,
  ChargeItemServiceResource,
  ChargeItemStatus,
  ChargeItemUpdate,
  getComponentsFromChargeItem,
  PriceComponentType,
} from "@/types/billing/chargeItem/chargeItem";
import chargeItemApi from "@/types/billing/chargeItem/chargeItemApi";
import { UserReadMinimal } from "@/types/user/user";
import { isPositive, round, zodDecimal } from "@/Utils/decimal";
import { ShortcutBadge } from "@/Utils/keyboardShortcutComponents";
import { handleHttpError } from "@/Utils/request/errorHandler";
import mutate from "@/Utils/request/mutate";
import { FieldErrors } from "react-hook-form";

const APPROVED_BY_OTHERS_OPTION_ID = "__others__";

const MAX_DISCOUNT_PERCENTAGE = 50;

const DISCOUNT_REASONS = [
  "Financial Hardship",
  "Employee/Staff Benefit",
  "CSR",
  "Billing Error",
  "Other",
] as const;

type DiscountApprovalSelection =
  | { kind: "none" }
  | { kind: "others"; label: string }
  | { kind: "user"; user: UserReadMinimal };

interface EditInvoiceTableProps {
  facilityId: string;
  chargeItems: ChargeItemRead[];
  onClose: () => void;
  onSuccess: () => void;
  enableShortcut?: boolean;
}

// Schema for a single price component
const priceComponentSchema = z.object({
  monetary_component_type: z.nativeEnum(MonetaryComponentType),
  code: z
    .object({
      code: z.string(),
      system: z.string(),
      display: z.string(),
    })
    .optional(),
  factor: zodDecimal({ min: 0, max: 100 }).optional().nullable(),
  amount: zodDecimal({ min: 0 }).optional().nullable(),
  conditions: z.array(conditionSchema).optional(),
});

const chargeItemBaseSchema = z.object({
  baseAmount: zodDecimal({ min: 0 }),
  quantity: zodDecimal({ min: 1 }),
  taxComponents: z.array(priceComponentSchema).optional(),
  discounts: z.array(priceComponentSchema).optional(),
});

const formSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      status: z.nativeEnum(ChargeItemStatus),
      description: z
        .string()
        .optional()
        .nullable()
        .transform((val) => (val === "" ? null : val)),
      note: z
        .string()
        .optional()
        .refine(
          (value) => {
            if (!value) return true;

            // predefined dropdown reasons
            if (
              DISCOUNT_REASONS.includes(
                value as (typeof DISCOUNT_REASONS)[number],
              )
            ) {
              return true;
            }

            // custom reason
            return value.length >= 10 && value.length <= 200;
          },
          {
            message: "Reason must be between 10 and 200 characters.",
          },
        ),
      ...chargeItemBaseSchema.shape,
    }),
  ),
});

type FormValues = z.infer<typeof formSchema>;
type DiscountFormValue = NonNullable<
  FormValues["items"][number]["discounts"]
>[number];

// Helper to extract all error messages from a row's errors object
function getRowErrors(
  rowErrors: FieldErrors<FormValues["items"][number]> | undefined,
): string[] {
  if (!rowErrors) return [];

  const errors: string[] = [];

  const extractErrors = (obj: unknown, prefix = ""): void => {
    if (!obj || typeof obj !== "object") return;

    const record = obj as Record<string, unknown>;

    if ("message" in record && typeof record.message === "string") {
      errors.push(prefix ? `${prefix}: ${record.message}` : record.message);
      return;
    }

    for (const [key, value] of Object.entries(record)) {
      if (key === "ref" || key === "type") continue;
      extractErrors(value, prefix ? `${prefix} › ${key}` : key);
    }
  };

  extractErrors(rowErrors);
  return errors;
}

export function EditInvoiceTable({
  facilityId,
  chargeItems,
  onClose,
  onSuccess,
  enableShortcut,
}: EditInvoiceTableProps) {
  const { t } = useTranslation();
  const { facility } = useCurrentFacility();
  useShortcutSubContext("facility:billing:invoice:show");
  const hasAppointmentChargeItems = chargeItems.some(
    (item) => item.service_resource === ChargeItemServiceResource.appointment,
  );
  const hasNonAppointmentChargeItems = chargeItems.some(
    (item) => item.service_resource !== ChargeItemServiceResource.appointment,
  );

  const isAppointmentChargeItem = (chargeItemId: string) =>
    chargeItems.some(
      (item) =>
        item.id === chargeItemId &&
        item.service_resource === ChargeItemServiceResource.appointment,
    );

  const [performers, setPerformers] = useState<
    Record<string, UserReadMinimal | undefined>
  >(() => {
    const initial: Record<string, UserReadMinimal | undefined> = {};
    chargeItems.forEach((item) => {
      initial[item.id] = item.performer_actor;
    });
    return initial;
  });
  const [discountApprovals, setDiscountApprovals] = useState<
    Record<string, DiscountApprovalSelection>
  >(() => {
    const initial: Record<string, DiscountApprovalSelection> = {};
    chargeItems.forEach((item) => {
      if (item.approved_by) {
        initial[item.id] = { kind: "user", user: item.approved_by };
        return;
      }

      if (item.approved_by_label) {
        initial[item.id] = { kind: "others", label: item.approved_by_label };
        return;
      }

      initial[item.id] = { kind: "none" };
    });
    return initial;
  });

  const handlePerformerChange = (
    chargeItemId: string,
    user: UserReadMinimal | undefined,
  ) => {
    setPerformers((prev) => ({
      ...prev,
      [chargeItemId]: user,
    }));
  };

  const handleApplyPerformerToAll = (user: UserReadMinimal | undefined) => {
    const newPerformers: Record<string, UserReadMinimal | undefined> = {};
    chargeItems.forEach((item) => {
      newPerformers[item.id] = user;
    });
    setPerformers(newPerformers);
  };

  const handleClearAllPerformers = () => {
    const newPerformers: Record<string, UserReadMinimal | undefined> = {};
    chargeItems.forEach((item) => {
      newPerformers[item.id] = undefined;
    });
    setPerformers(newPerformers);
  };

  const handleApprovedByUserChange = (
    chargeItemId: string,
    user: UserReadMinimal,
  ) => {
    setDiscountApprovals((prev) => ({
      ...prev,
      [chargeItemId]: { kind: "user", user },
    }));
  };

  const handleApprovedByOthersSelect = (chargeItemId: string) => {
    setDiscountApprovals((prev) => ({
      ...prev,
      [chargeItemId]: {
        kind: "others",
        label:
          prev[chargeItemId]?.kind === "others" ? prev[chargeItemId].label : "",
      },
    }));
  };

  const handleApprovedByOthersLabelChange = (
    chargeItemId: string,
    label: string,
  ) => {
    setDiscountApprovals((prev) => ({
      ...prev,
      [chargeItemId]: { kind: "others", label },
    }));
  };

  const getDiscountComponentKey = (
    component: MonetaryComponent | undefined,
  ) => {
    if (!component?.code?.code) return undefined;
    return `${component.code.system}::${component.code.code}`;
  };

  const hasAppliedDiscountValues = (
    discounts: FormValues["items"][number]["discounts"] | undefined,
  ) => {
    return (discounts || []).some((discount) => {
      const hasAmount = discount.amount && isPositive(discount.amount);
      const hasFactor = discount.factor != null && isPositive(discount.factor);
      return hasAmount || hasFactor;
    });
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      items: chargeItems.map((item) => {
        const baseComponent = getComponentsFromChargeItem(
          item,
          MonetaryComponentType.base,
          PriceComponentType.unit_price,
        )[0];
        const discountComponents = getComponentsFromChargeItem(
          item,
          MonetaryComponentType.discount,
          PriceComponentType.unit_price,
        );
        const nonDiscountComponents = (item.unit_price_components || []).filter(
          (component) =>
            component.monetary_component_type !== MonetaryComponentType.base &&
            component.monetary_component_type !==
              MonetaryComponentType.discount,
        );

        const discounts = discountComponents.map((component) => ({
          ...component,
          factor: component.factor ? round(component.factor) : component.factor,
          amount: component.amount ? round(component.amount) : component.amount,
          conditions: component.conditions?.map((condition) => ({
            ...condition,
            _conditionType: getConditionDiscriminatorValue(
              condition.metric,
              condition.operation,
            ),
          })),
        }));
        const normalizedDiscounts = isAppointmentChargeItem(item.id)
          ? discounts.length > 0
            ? discounts
            : [
                {
                  monetary_component_type: MonetaryComponentType.discount,
                  amount: null,
                  factor: null,
                  conditions: [],
                },
              ]
          : discounts;

        return {
          id: item.id,
          title: item.title,
          status: item.status as ChargeItemStatus,
          description: item.description || "",
          note: item.note || "",
          baseAmount: round(baseComponent?.amount || "0"),
          quantity: round(item.quantity),
          taxComponents: nonDiscountComponents,
          discounts: normalizedDiscounts,
        };
      }),
    },
  });

  const { mutate: updateChargeItems, isPending } = useMutation({
    mutationFn: mutate(chargeItemApi.upsertChargeItem, {
      pathParams: { facilityId },
    }),
    onSuccess: () => {
      toast.success(t("invoice_updated_successfully"));

      onSuccess();
      onClose();
    },
    onError: (error) => {
      handleHttpError(error);
    },
  });

  const onSubmit = (data: FormValues) => {
    for (const item of data.items) {
      for (const discount of item.discounts || []) {
        if (
          discount.factor != null &&
          Number(discount.factor) > MAX_DISCOUNT_PERCENTAGE &&
          Number(discount.factor) < 100
        ) {
          toast.error(`Discount cannot exceed ${MAX_DISCOUNT_PERCENTAGE}%.`);
          return;
        }

        // Amount discount validation
      }

      const priceComponents = [
        {
          monetary_component_type: MonetaryComponentType.base,
          amount: item.baseAmount,
        },
        ...(item.taxComponents || []).filter(
          (component) =>
            component.monetary_component_type !== MonetaryComponentType.tax,
        ),
        ...(item.discounts || []).filter(
          (discount) =>
            (discount.amount != null && isPositive(discount.amount)) ||
            (discount.factor != null && isPositive(discount.factor)),
        ),
      ];
      if (hasExcessiveDiscount(priceComponents)) {
        toast.error("Combined discounts cannot exceed the item price.");
        return;
      }
    }
    const hasMissingApprover = data.items.some((item) => {
      if (!isAppointmentChargeItem(item.id)) {
        return false;
      }

      const hasDiscounts = hasAppliedDiscountValues(item.discounts);

      if (!hasDiscounts) {
        return false;
      }

      const approval = discountApprovals[item.id] ?? { kind: "none" };

      if (approval.kind === "none") {
        return true;
      }

      if (approval.kind === "others") {
        return !approval.label.trim();
      }

      return false;
    });

    if (hasMissingApprover) {
      toast.error(t("select_approved_by_for_discounted_items"));
      return;
    }

    const updates: ChargeItemUpdate[] = data.items.map((item) => {
      const appliedDiscounts = (item.discounts || []).filter((discount) => {
        const hasAmount = discount.amount && isPositive(discount.amount);
        const hasFactor =
          discount.factor != null && isPositive(discount.factor);
        return hasAmount || hasFactor;
      });
      const discountApproval = discountApprovals[item.id] ?? { kind: "none" };

      return {
        id: item.id,
        title: item.title,
        status: item.status as ChargeItemStatus,
        quantity: item.quantity,
        unit_price_components: [
          {
            monetary_component_type: MonetaryComponentType.base,
            amount: item.baseAmount,
            conditions: [],
          },
          ...(item.taxComponents || []),
          ...appliedDiscounts,
        ],
        description: item.description || undefined,
        note: item.note?.trim() || undefined,
        performer_actor: performers[item.id]?.id,
        ...(isAppointmentChargeItem(item.id)
          ? {
              approved_by:
                appliedDiscounts.length > 0 && discountApproval.kind === "user"
                  ? discountApproval.user.id
                  : null,
              approved_by_label:
                appliedDiscounts.length > 0 &&
                discountApproval.kind === "others"
                  ? discountApproval.label.trim()
                  : null,
            }
          : {}),
      };
    });
    const hasCompleteWaiver = updates.some((item) =>
      item.unit_price_components?.some(
        (component) =>
          component.factor != null && Number(component.factor) === 100,
      ),
    );

    if (hasCompleteWaiver) {
      const proceed = window.confirm("You Are Giving 100% Discount. Proceed?");

      if (!proceed) {
        return;
      }
    }
    updateChargeItems({ datapoints: updates });
  };

  const handleBaseAmountChange = (index: number, value: string) => {
    form.setValue(`items.${index}.baseAmount`, value);
  };

  const handleAddDiscount = (itemIndex: number) => {
    const currentDiscounts =
      form.getValues(`items.${itemIndex}.discounts`) || [];
    form.setValue(`items.${itemIndex}.discounts`, [
      ...currentDiscounts,
      {
        monetary_component_type: MonetaryComponentType.discount,
        code: undefined,
        conditions: [],
        amount: null,
        factor: null,
      },
    ]);
  };

  const handleRemoveDiscount = (itemIndex: number, discountIndex: number) => {
    const currentDiscounts =
      form.getValues(`items.${itemIndex}.discounts`) || [];
    form.setValue(
      `items.${itemIndex}.discounts`,
      currentDiscounts.filter((_, idx) => idx !== discountIndex),
    );
  };

  // Get discounts from facility settings
  const globalDiscounts = [
    ...(facility?.discount_monetary_components || []),
    ...(facility?.instance_discount_monetary_components || []),
  ].filter((d) => d != null);

  const wouldExceedItemPrice = (
    itemIndex: number,
    discounts: FormValues["items"][number]["discounts"],
  ) => {
    const item = form.getValues(`items.${itemIndex}`);
    return hasExcessiveDiscount([
      {
        monetary_component_type: MonetaryComponentType.base,
        amount: item.baseAmount,
      },
      ...(item.taxComponents || []).filter(
        (component) =>
          component.monetary_component_type !== MonetaryComponentType.tax,
      ),
      ...(discounts || []).map((discount) => ({
        ...discount,
        amount: discount.amount === "" ? "0" : discount.amount,
        factor: discount.factor === "" ? "0" : discount.factor,
      })),
    ]);
  };

  const setDiscountIfValid = (
    itemIndex: number,
    discountIndex: number,
    discount: DiscountFormValue,
  ) => {
    const discounts = [
      ...(form.getValues(`items.${itemIndex}.discounts`) || []),
    ];
    discounts[discountIndex] = discount;
    if (wouldExceedItemPrice(itemIndex, discounts)) {
      toast.error("Combined discounts cannot exceed the item price.");
      return false;
    }
    form.setValue(`items.${itemIndex}.discounts`, discounts, {
      shouldDirty: true,
      shouldValidate: true,
    });
    return true;
  };

  const handleDiscountComponentChange = (
    itemIndex: number,
    discountIndex: number,
    componentKey: string,
  ) => {
    const selectedComponent = globalDiscounts.find(
      (c) => getDiscountComponentKey(c) === componentKey,
    );

    if (selectedComponent) {
      setDiscountIfValid(itemIndex, discountIndex, {
        ...selectedComponent,
        conditions:
          selectedComponent.conditions?.map((condition) => ({
            ...condition,
            _conditionType: getConditionDiscriminatorValue(
              condition.metric,
              condition.operation,
            ),
          })) || [],
      });
    }
  };

  const handleDiscountTypeToggle = (
    itemIndex: number,
    discountIndex: number,
    checked: boolean,
  ) => {
    const discount = form.getValues(
      `items.${itemIndex}.discounts.${discountIndex}`,
    );
    const item = form.getValues(`items.${itemIndex}`);
    const discountableAmount = getDiscountableAmount([
      {
        monetary_component_type: MonetaryComponentType.base,
        amount: item.baseAmount,
      },
      ...(item.taxComponents || []).filter(
        (component) =>
          component.monetary_component_type !== MonetaryComponentType.tax,
      ),
    ]);
    const convertedValue = convertDiscountValue(
      discount,
      discountableAmount,
      checked,
    );

    if (checked) {
      setDiscountIfValid(itemIndex, discountIndex, {
        ...discount,
        factor: convertedValue,
        amount: null,
      });
    } else {
      setDiscountIfValid(itemIndex, discountIndex, {
        ...discount,
        amount: convertedValue,
        factor: null,
      });
    }
  };

  const handleApplyGlobalDiscount = (discountKey: string) => {
    const items = form.getValues("items");

    // Find the discount definition from the global discounts
    const discountDefinition = globalDiscounts.find(
      (d) => getDiscountComponentKey(d) === discountKey,
    );

    if (!discountDefinition) return;

    items.forEach((item, itemIndex) => {
      const currentDiscounts = item.discounts || [];
      // Check if this discount is already applied
      const existingIndex = currentDiscounts.findIndex(
        (d) => getDiscountComponentKey(d) === discountKey,
      );

      if (existingIndex === -1) {
        // Add the discount if not already present
        const newDiscount = {
          ...discountDefinition,
          conditions:
            discountDefinition.conditions?.map((condition) => ({
              ...condition,
              _conditionType: getConditionDiscriminatorValue(
                condition.metric,
                condition.operation,
              ),
            })) || [],
        };
        const nextDiscounts = [...currentDiscounts, newDiscount];
        if (wouldExceedItemPrice(itemIndex, nextDiscounts)) {
          toast.error(
            `${item.title}: combined discounts cannot exceed the item price.`,
          );
          return;
        }
        form.setValue(`items.${itemIndex}.discounts`, nextDiscounts, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    });
  };

  const handleClearAllDiscounts = () => {
    const items = form.getValues("items");
    items.forEach((_, itemIndex) => {
      form.setValue(`items.${itemIndex}.discounts`, []);
    });
  };

  if (chargeItems.length === 0) {
    return <div>{t("no_charge_items_found")}</div>;
  }

  const filteredDiscounts = globalDiscounts.filter(getDiscountComponentKey);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Table className="border">
            <TableHeader>
              <TableRow className="divide-x font-semibold">
                <TableHead className="w-12">#</TableHead>
                <TableHead>{t("item")}</TableHead>
                <TableHead>
                  <div className="flex items-center justify-center gap-2">
                    {t("performer")}
                    {chargeItems.length > 1 && (
                      <UserSelector
                        selected={undefined}
                        onChange={handleApplyPerformerToAll}
                        facilityId={facilityId}
                        trigger={
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                          >
                            {t("apply_to_all")}
                            <ChevronDown className="h-3 w-3 ml-1" />
                          </Button>
                        }
                        contentAlign="center"
                        contentClassName="w-80"
                        onClear={handleClearAllPerformers}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead className="min-w-[150px]">
                  {t("unit_price")} ({getCurrencySymbol()})
                </TableHead>
                <TableHead className="min-w-[100px]">{t("quantity")}</TableHead>
                {hasAppointmentChargeItems && (
                  <TableHead className="min-w-[220px]">
                    {t("approved_by")}
                  </TableHead>
                )}
                <TableHead>
                  <div className="flex items-center justify-center gap-2">
                    {t("discounts")}
                    {hasNonAppointmentChargeItems &&
                      filteredDiscounts.length > 0 &&
                      chargeItems.length > 1 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                            >
                              {t("apply_to_all")}
                              <ChevronDown className="h-3 w-3 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {filteredDiscounts.map((discount) => {
                              const key = getDiscountComponentKey(discount);
                              return (
                                <DropdownMenuItem
                                  key={key}
                                  onClick={() =>
                                    key && handleApplyGlobalDiscount(key)
                                  }
                                >
                                  {discount.code?.display} @{" "}
                                  <MonetaryDisplay {...discount} />
                                </DropdownMenuItem>
                              );
                            })}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={handleClearAllDiscounts}
                              className="text-destructive"
                            >
                              {t("clear_all")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {form.watch("items").map((item, index) => {
                const rowErrors = getRowErrors(
                  form.formState.errors.items?.[index],
                );
                const isAppointmentItem = isAppointmentChargeItem(item.id);

                return (
                  <React.Fragment key={item.id}>
                    <TableRow className="divide-x font-medium text-gray-950">
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{item.title}</TableCell>
                      <TableCell>
                        <UserSelector
                          selected={performers[item.id]}
                          onChange={(user) =>
                            handlePerformerChange(item.id, user)
                          }
                          facilityId={facilityId}
                          placeholder={t("select_performer")}
                        />
                      </TableCell>
                      <TableCell>
                        <FormField
                          control={form.control}
                          name={`items.${index}.baseAmount`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <MonetaryAmountInput
                                  {...field}
                                  value={field.value ?? "0"}
                                  onChange={(e) => {
                                    field.onChange(e.target.value);
                                    handleBaseAmountChange(
                                      index,
                                      e.target.value,
                                    );
                                  }}
                                  placeholder="0.00"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <FormField
                          control={form.control}
                          name={`items.${index}.quantity`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  min="1"
                                  step="1"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </TableCell>
                      {hasAppointmentChargeItems && (
                        <TableCell>
                          {isAppointmentItem ? (
                            (() => {
                              const hasAppliedDiscounts =
                                hasAppliedDiscountValues(item.discounts);

                              if (!hasAppliedDiscounts) {
                                return (
                                  <div className="text-sm text-gray-500 py-2">
                                    {t("no_discounts")}
                                  </div>
                                );
                              }

                              const approval = discountApprovals[item.id];

                              return (
                                <div className="space-y-2">
                                  <UserSelector
                                    selected={
                                      approval?.kind === "user"
                                        ? approval.user
                                        : undefined
                                    }
                                    selectedCustomOptionId={
                                      approval?.kind === "others"
                                        ? APPROVED_BY_OTHERS_OPTION_ID
                                        : undefined
                                    }
                                    onChange={(user) =>
                                      handleApprovedByUserChange(item.id, user)
                                    }
                                    onCustomOptionSelect={() =>
                                      handleApprovedByOthersSelect(item.id)
                                    }
                                    customOptions={[
                                      {
                                        id: APPROVED_BY_OTHERS_OPTION_ID,
                                        label: t("others"),
                                      },
                                    ]}
                                    facilityId={facilityId}
                                    placeholder={t("select_approved_by")}
                                  />
                                  {approval?.kind === "others" && (
                                    <Input
                                      value={approval.label}
                                      onChange={(event) =>
                                        handleApprovedByOthersLabelChange(
                                          item.id,
                                          event.target.value,
                                        )
                                      }
                                      placeholder={t("specify_approved_by")}
                                    />
                                  )}
                                </div>
                              );
                            })()
                          ) : (
                            <div className="text-sm text-gray-500 py-2">-</div>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="border-r border-gray-200 font-medium text-gray-950 text-sm ">
                        {(() => {
                          const hasAppliedDiscounts =
                            item.discounts && item.discounts.length > 0;

                          if (isAppointmentItem) {
                            const discount = item.discounts?.[0];
                            const isPercentage = discount
                              ? isPercentageBased(discount)
                              : false;
                            const value = isPercentage
                              ? (discount?.factor ?? "0")
                              : (discount?.amount ?? "0");

                            return (
                              <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 min-w-20">
                                    <MonetaryAmountInput
                                      hideCurrency={true}
                                      value={value}
                                      onChange={(e) => {
                                        const newValue = e.target.value;
                                        setDiscountIfValid(index, 0, {
                                          ...discount,
                                          monetary_component_type:
                                            MonetaryComponentType.discount,
                                          factor: isPercentage
                                            ? newValue
                                            : null,
                                          amount: isPercentage
                                            ? null
                                            : newValue,
                                        });
                                      }}
                                      placeholder="0.00"
                                    />
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-sm text-gray-500">
                                      {getCurrencySymbol()}
                                    </span>
                                    <Switch
                                      checked={isPercentage}
                                      onCheckedChange={(checked) => {
                                        handleDiscountTypeToggle(
                                          index,
                                          0,
                                          checked,
                                        );
                                      }}
                                      className="data-[state=unchecked]:bg-gray-900"
                                    />
                                    <span className="text-sm text-gray-500">
                                      %
                                    </span>
                                  </div>
                                </div>

                                <FormField
                                  control={form.control}
                                  name={`items.${index}.note`}
                                  render={({ field }) => {
                                    const predefinedReason =
                                      DISCOUNT_REASONS.includes(
                                        field.value as (typeof DISCOUNT_REASONS)[number],
                                      )
                                        ? field.value
                                        : "Other";

                                    return (
                                      <FormItem>
                                        <FormLabel>{t("reason")}</FormLabel>

                                        <Select
                                          value={predefinedReason}
                                          onValueChange={(value) => {
                                            if (value === "Other") {
                                              field.onChange("");
                                            } else {
                                              field.onChange(value);
                                            }
                                          }}
                                        >
                                          <FormControl>
                                            <SelectTrigger>
                                              <SelectValue placeholder="Select reason" />
                                            </SelectTrigger>
                                          </FormControl>

                                          <SelectContent>
                                            {DISCOUNT_REASONS.map((reason) => (
                                              <SelectItem
                                                key={reason}
                                                value={reason}
                                              >
                                                {reason}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>

                                        {predefinedReason === "Other" && (
                                          <>
                                            <Textarea
                                              value={
                                                DISCOUNT_REASONS.includes(
                                                  field.value as (typeof DISCOUNT_REASONS)[number],
                                                )
                                                  ? ""
                                                  : field.value || ""
                                              }
                                              onChange={(e) =>
                                                field.onChange(e.target.value)
                                              }
                                              placeholder="Specify reason"
                                              className="mt-3 min-h-20"
                                              maxLength={200}
                                            />

                                            <p className="text-xs text-gray-500 mt-1">
                                              Minimum 10 characters. Maximum 200
                                              characters.
                                            </p>
                                          </>
                                        )}

                                        <FormMessage />
                                      </FormItem>
                                    );
                                  }}
                                />
                              </div>
                            );
                          }

                          // Show "no discounts" only if no global discounts available AND no discounts applied
                          if (
                            globalDiscounts.length === 0 &&
                            !hasAppliedDiscounts
                          ) {
                            return (
                              <div className="text-sm text-gray-500 py-2">
                                {t("no_discounts")}
                              </div>
                            );
                          }

                          const hasEmptyRow =
                            item.discounts?.some((d) => !d.code) || false;

                          const hasMoreDiscountsToAdd =
                            (item.discounts?.length || 0) <
                            globalDiscounts.length;

                          return (
                            <div className="space-y-2">
                              {item.discounts &&
                                item.discounts.length > 0 &&
                                item.discounts.map(
                                  (discount, discountIndex) => (
                                    <div
                                      key={discountIndex}
                                      className="flex items-center gap-2"
                                    >
                                      <FormField
                                        key={`${discountIndex}-code`}
                                        control={form.control}
                                        name={`items.${index}.discounts.${discountIndex}.code`}
                                        render={() => {
                                          const selectedDiscountKeys =
                                            item.discounts
                                              ?.filter(
                                                (_, idx) =>
                                                  idx !== discountIndex,
                                              )
                                              .map((d) =>
                                                getDiscountComponentKey(d),
                                              )
                                              .filter((key) => key) || [];

                                          const filteredDiscounts =
                                            globalDiscounts.filter(
                                              (component) => {
                                                const key =
                                                  getDiscountComponentKey(
                                                    component,
                                                  );
                                                return (
                                                  key &&
                                                  !selectedDiscountKeys.includes(
                                                    key,
                                                  )
                                                );
                                              },
                                            );

                                          const currentKey =
                                            getDiscountComponentKey(discount);
                                          const currentDiscount =
                                            globalDiscounts.find(
                                              (c) =>
                                                getDiscountComponentKey(c) ===
                                                currentKey,
                                            );

                                          return (
                                            <FormItem className="flex-1">
                                              <FormControl>
                                                <Select
                                                  value={currentKey || ""}
                                                  onValueChange={(value) => {
                                                    handleDiscountComponentChange(
                                                      index,
                                                      discountIndex,
                                                      value,
                                                    );
                                                  }}
                                                >
                                                  <SelectTrigger>
                                                    <SelectValue
                                                      placeholder={t(
                                                        "select_discount",
                                                      )}
                                                    >
                                                      {currentDiscount && (
                                                        <>
                                                          {
                                                            currentDiscount.code
                                                              ?.display
                                                          }{" "}
                                                          @
                                                          <MonetaryDisplay
                                                            {...currentDiscount}
                                                          />
                                                        </>
                                                      )}
                                                    </SelectValue>
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    {filteredDiscounts.map(
                                                      (component) => {
                                                        const key =
                                                          getDiscountComponentKey(
                                                            component,
                                                          );
                                                        return (
                                                          <SelectItem
                                                            key={key}
                                                            value={key || ""}
                                                          >
                                                            {
                                                              component.code
                                                                ?.display
                                                            }{" "}
                                                            @
                                                            <MonetaryDisplay
                                                              {...component}
                                                            />
                                                          </SelectItem>
                                                        );
                                                      },
                                                    )}
                                                  </SelectContent>
                                                </Select>
                                              </FormControl>
                                            </FormItem>
                                          );
                                        }}
                                      />
                                      <FormField
                                        key={`${discountIndex}-amount`}
                                        control={form.control}
                                        name={`items.${index}.discounts.${discountIndex}`}
                                        render={() => {
                                          const isDisabled = !discount?.code;
                                          const isPercentage =
                                            discount &&
                                            isPercentageBased(discount);
                                          const value = isPercentage
                                            ? (discount?.factor ?? "0")
                                            : (discount?.amount ?? "0");

                                          return (
                                            <FormItem className="flex-1 min-w-20">
                                              <FormControl>
                                                <MonetaryAmountInput
                                                  hideCurrency={true}
                                                  value={value}
                                                  onChange={(e) => {
                                                    const newValue =
                                                      e.target.value;

                                                    setDiscountIfValid(
                                                      index,
                                                      discountIndex,
                                                      {
                                                        ...discount,
                                                        factor: isPercentage
                                                          ? newValue
                                                          : null,
                                                        amount: isPercentage
                                                          ? null
                                                          : newValue,
                                                      },
                                                    );
                                                  }}
                                                  placeholder="0.00"
                                                  disabled={isDisabled}
                                                />
                                              </FormControl>
                                            </FormItem>
                                          );
                                        }}
                                      />
                                      <FormField
                                        key={`${discountIndex}-type`}
                                        control={form.control}
                                        name={`items.${index}.discounts.${discountIndex}`}
                                        render={() => {
                                          const isDisabled = !discount?.code;
                                          const isPercentage =
                                            discount &&
                                            isPercentageBased(discount);

                                          return (
                                            <FormItem>
                                              <div className="flex items-center gap-2">
                                                <span className="text-sm text-gray-500">
                                                  {getCurrencySymbol()}
                                                </span>
                                                <FormControl>
                                                  <Switch
                                                    checked={isPercentage}
                                                    onCheckedChange={(
                                                      checked,
                                                    ) => {
                                                      handleDiscountTypeToggle(
                                                        index,
                                                        discountIndex,
                                                        checked,
                                                      );
                                                    }}
                                                    disabled={isDisabled}
                                                    className="data-[state=unchecked]:bg-gray-900"
                                                  />
                                                </FormControl>
                                                <span className="text-sm text-gray-500">
                                                  %
                                                </span>
                                              </div>
                                            </FormItem>
                                          );
                                        }}
                                      />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 shrink-0"
                                        onClick={() =>
                                          handleRemoveDiscount(
                                            index,
                                            discountIndex,
                                          )
                                        }
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  ),
                                )}
                              {hasMoreDiscountsToAdd && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="w-full"
                                  onClick={() => handleAddDiscount(index)}
                                  disabled={hasEmptyRow}
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  {t("add_discount")}
                                </Button>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                    {rowErrors.length > 0 && (
                      <TableRow className="bg-red-50 hover:bg-red-50">
                        <TableCell
                          colSpan={hasAppointmentChargeItems ? 7 : 6}
                          className="py-2"
                        >
                          <ul className="list-disc list-inside text-sm text-red-600 space-y-0.5">
                            {rowErrors.map((error, i) => (
                              <li key={i}>{error}</li>
                            ))}
                          </ul>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="sticky bottom-0 bg-white p-4 flex justify-end gap-2 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("cancel")}
            {enableShortcut && <ShortcutBadge actionId="cancel-action" />}
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? t("saving") : t("save")}
            {enableShortcut && <ShortcutBadge actionId="submit-action" />}
          </Button>
        </div>
      </form>
    </Form>
  );
}
